import { useState } from "react";
import { ClockCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { GhostButton, SchemaModalShell } from "../../../reservations/restaurant-schema/modals/SchemaModalShell";

type GuideTab = "reports" | "reservations";
const GUIDE_TABS: GuideTab[] = ["reports", "reservations"];

// Примерът: три дни от една седмица — обикновен, след полунощ и почивен.
// Резултатът на всеки ред е ключ под `workingHours.guide.result.<tab>.<day>`.
const EXAMPLE_DAYS = [
  { day: "tuesday", hours: "10:00 – 22:00", afterMidnight: false, closed: false },
  { day: "friday", hours: "12:00 – 02:00", afterMidnight: true, closed: false },
  { day: "monday", hours: null, afterMidnight: false, closed: true },
] as const;

// Случаите под таблицата, в реда на екрана — `workingHours.guide.notes.<tab>.<ключ>`
// с `title` (кратко име на случая) и `text` (конкретен пример с дни и часове).
const NOTES: Record<GuideTab, string[]> = {
  reports: ["afterMidnight", "closedAfterMidnight", "notSet"],
  reservations: ["lastSlot", "afterMidnight", "closed", "schedule", "notSet"],
};

/**
 * „Как работи работното време" — примерите за справките и за резервациите:
 * таблица с три дни (обикновен, след полунощ, почивен) и какво дава всеки,
 * после отделните случаи — всеки със заглавие и пример с конкретни дни. Текстовете описват `lib/businessDay.ts` и
 * `reservations/logic/workingSlots.ts` — смениш ли правилата там, смени ги и
 * тук (`workingHours.guide` в преводите).
 */
export function WorkingHoursGuideModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<GuideTab>("reports");

  return (
    <SchemaModalShell
      open={open}
      size="wide"
      icon={<ClockCircleOutlined />}
      title={t("workingHours.guide.modalTitle")}
      subtitle={t("workingHours.guide.modalSubtitle")}
      onClose={onClose}
      footer={<GhostButton onClick={onClose}>{t("workingHours.guide.close")}</GhostButton>}
    >
      <div className="flex flex-col gap-4">
        {/* Двата таба — центрирани, с еднаква ширина; избраният е повдигнат. */}
        <div className="flex justify-center">
          <div
            role="tablist"
            className="grid w-full max-w-[340px] grid-cols-2 gap-1 rounded-[12px] border border-shade-2 bg-fill p-1"
          >
            {GUIDE_TABS.map((guideTab) => {
              const selected = guideTab === tab;
              return (
                <button
                  key={guideTab}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setTab(guideTab)}
                  className={`h-9 cursor-pointer rounded-[9px] border-none text-[13.5px] font-bold tracking-[-0.2px] transition-[background,color,box-shadow] duration-150 ${
                    selected
                      ? "bg-surface text-brand-deep-ink shadow-[0_1px_3px_rgba(17,17,17,0.14)]"
                      : "bg-transparent text-muted hover:text-ink"
                  }`}
                >
                  {t(`workingHours.guide.tab.${guideTab}`)}
                </button>
              );
            })}
          </div>
        </div>

        <p className="m-0 text-[14px] font-semibold leading-[1.45] text-ink">
          {t(`workingHours.guide.lead.${tab}`)}
        </p>

        <div className="overflow-hidden rounded-xl border border-shade-2">
          <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1.3fr)] gap-3 bg-fill px-3.5 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted">
            <span>{t("workingHours.guide.columnDay")}</span>
            <span>{t("workingHours.guide.columnHours")}</span>
            <span>{t(`workingHours.guide.columnResult.${tab}`)}</span>
          </div>
          <div className="divide-y divide-shade-2">
            {EXAMPLE_DAYS.map((example) => (
              <div
                key={example.day}
                className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,1.3fr)] items-center gap-3 px-3.5 py-3 text-[13px]"
              >
                <span className="font-bold text-ink">{t(`workingHours.days.${example.day}`)}</span>
                <span className="text-body">
                  {example.closed ? (
                    t("workingHours.closedDay")
                  ) : (
                    <>
                      <span className="tabular-nums">{example.hours}</span>
                      {example.afterMidnight && (
                        <span className="block text-[11.5px] text-muted">
                          {t("workingHours.guide.afterMidnight")}
                        </span>
                      )}
                    </>
                  )}
                </span>
                <span
                  className={`font-bold tabular-nums ${example.closed && tab === "reservations" ? "text-danger-ink" : "text-brand-deep-ink"}`}
                >
                  {t(`workingHours.guide.result.${tab}.${example.day}`)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted">
            {t("workingHours.guide.goodToKnow")}
          </div>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {NOTES[tab].map((note) => (
              <li key={note} className="rounded-[10px] border border-shade-2 bg-fill px-3.5 py-2.5">
                <div className="text-[13px] font-bold text-ink">
                  {t(`workingHours.guide.notes.${tab}.${note}.title`)}
                </div>
                <div className="mt-0.5 text-[13px] leading-[1.5] text-body">
                  {t(`workingHours.guide.notes.${tab}.${note}.text`)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </SchemaModalShell>
  );
}
